/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { HAWAII_TIMEZONE } from 'blb-constants/global';
import { getConfig } from 'blb-utils/config';
import { getPersona, isPipelinePersona } from 'blb-persona/index';
import { FilterPreset } from 'blb-constants/pipelineFilter';
export const PIPELINE_STATE_KEY = 'pipelineState';
export const PIPELINE_FILTERS_KEY = 'pipelineFilters';
export const DEFAULT_SORT_FIELD = 'Eclose.closingDatePipeline';
const DEFAULT_SORT_DIR = 'desc';
export const DEFAULT_PIPELINE_STATE = {
  offset: 0,
  sortDir: DEFAULT_SORT_DIR,
  sortField: DEFAULT_SORT_FIELD
};
export const DEFAULT_PIPELINE_FILTERS = {
  opsDeskAssigneeId: '',
  opsDeskAssignment: undefined,
  showClosingsWithTasks: true,
  showCurrentClosings: true,
  showHybridClosing: true,
  showRONBSRClosing: true,
  showRONClosing: true,
  showRONNotaryCamClosing: false,
  showRONOtherClosing: false,
  showSentClosings: true,
  showTraditionalClosing: false,
  showUnassignedClosings: true
};
export const getDefaultPipelineFiltersWithLoggedInUser = principalId => {
  if (!principalId) {
    principalId = '';
  }
  return {
    ...DEFAULT_PIPELINE_FILTERS,
    opsDeskAssigneeId: principalId
  };
};
export const DEFAULT_OPS_DESK_VIEWABLE_CLOSING_STATUSES = ['SENT', 'SENT_DAY_OF'];
export const DEFAULT_OPS_DESK_VIEWABLE_CLOSING_TYPES = ['HYBRID', 'RON'];
export const DEFAULT_OPS_DESK_VIEWABLE_RON_PLATFORMS = ['BLEND_SIGNING_ROOM', 'HYBRID'];

// [] stands for null in Elastic Search
export const EMPTY_ASSIGNED_CLOSINGS_FILTER_CONTENT = [[], ''];
export const NUM_DAYS_INCLUDED_AS_PRESENT = 4;

/**
 * Ensure that the sessionStorage value for PIPELINE_STATE_KEY has all
 * required keys or is empty (has no keys).
 * If not sanitized, clear the value so that it can be set to the default.
 */
export const sanitizePipelineState = () => {
  const pipelineStateString = sessionStorage.getItem(PIPELINE_STATE_KEY) || '{}';
  const pipelineState = JSON.parse(pipelineStateString);
  if (isEmpty(pipelineState)) {
    return;
  }
  const pipelineStateKeys = Object.keys(DEFAULT_PIPELINE_STATE);
  const isSanitized = pipelineStateKeys.every(key => pipelineState.hasOwnProperty(key));
  if (!isSanitized) {
    sessionStorage.removeItem(PIPELINE_STATE_KEY);
  }
};
export const getSortKey = (columnSchemas, sortField) => {
  return columnSchemas[sortField]?.sortKey ? columnSchemas[sortField].sortKey : sortField;
};

/**
 * This is a reverse lookup in the column schema to determine the column name of a given sort key
 * @param  {{[field:string]:Pipeline.FieldSchema}} columnSchemas
 * @param  {string} key The original sort key if it exists, otherwise the column name
 */
export const getOriginalColNameFromSchema = (columnSchemas, key) => {
  // Disabling the rule here because columnSchemas is a const that doesn't
  // inherit from the object prototype
  // eslint-disable-next-line guard-for-in
  for (const colName in columnSchemas) {
    if (key === colName) return colName;
    if (columnSchemas[colName].sortKey === key) return colName;
  }
  return undefined;
};

/**
 * This function returns the milliseconds of startOf day in UTC for today's date in Hawaii Time
 *
 * Since Eclose sends the closing date to Bridge as UTC, we:
 * - Start with UTC
 * - Convert the UTC to HST to get the USA date
 * - Then get the UTC start of the USA date
 */
export const getLatestUSTimezoneStartMinusDays = days => {
  // Using DateTime.utc() only will cause issues as Day in UTC changes before other timezones in USA
  const nowHawaiiTime = DateTime.utc().setZone(HAWAII_TIMEZONE);
  return DateTime.utc(nowHawaiiTime.year, nowHawaiiTime.month, nowHawaiiTime.day).minus({
    days
  }).startOf('day').valueOf();
};

/**
 * This function calculates the time boundary {startMs, endMs} for closingDate
 * - startMs: is the beginning of current date at UTC time zone
 * - endMs: take currentDate, go ahead a numberOfNextWeekDay, then take the end of day at Hawaii time zone
 *
 * Note that: this calculation can incorrectly include closings of other days. This is a known issue.
 * For example:
 * Let's say local current date is 2023-01-01, numberOfNextDay = 0
 * - startMs: 2023-01-01 00:00:00Z
 * - endMs: 2023-01-01 23:59:59 HST which means 2023-01-02 09:59:59 UTC
 *
 * For closing on 2023-01-02 at San Francisco, MrCooper has `closingDate` index value : 2023-01-02 00:00:00 PST,
 * which means 2023-01-02 07:00:00 (UTC). This value is less than endMs above. Thus, this closing is incorrectly included
 * in the search result. However, we deem that it's still fine if we provide to SA more data than necessary.
 * */
export const getClosingDateRange = numberOfNextWeekDay => {
  const localTime = DateTime.local();
  const startTime = DateTime.utc(localTime.year, localTime.month, localTime.day).startOf('day');
  let endTime = localTime;
  for (let i = 0; i < numberOfNextWeekDay; i++) {
    endTime = endTime.plus({
      days: 1
    });
    // skip saturday and sundday
    while ([6, 7].includes(endTime.weekday)) {
      endTime = endTime.plus({
        days: 1
      });
    }
  }
  endTime = endTime.setZone(HAWAII_TIMEZONE).endOf('day');

  // for start timestamp, use UTC
  return {
    startMs: startTime.toMillis(),
    endMs: endTime.toMillis()
  };
};

/**
 * This function formats filterParams into Elastic Search format
 * @param filterParams
 * @param principalType
 * @returns
 */
export const getFilters = (filterParams, me) => {
  const persona = getPersona(me.principalType);
  return isPipelinePersona(persona) ? persona.getPipelineFilters(filterParams) : [];
};
export const getFiltersForOpsDesk = filterParams => {
  const filters = [];
  const {
    enableAutoAssignment
  } = getConfig();
  const {
    showHybridClosing,
    showRONClosing,
    showRONBSRClosing,
    showRONNotaryCamClosing,
    showRONOtherClosing,
    showTraditionalClosing,
    opsDeskAssigneeId,
    showClosingsWithTasks,
    showUnassignedClosings,
    showSentClosings,
    showCurrentClosings
  } = filterParams;
  const selectedClosingType = [showHybridClosing ? 'HYBRID' : '', showRONClosing ? 'RON' : '', showTraditionalClosing ? 'TRADITIONAL' : ''].filter(Boolean);
  filters.push({
    columnField: 'Eclose.closingType',
    content: selectedClosingType,
    operator: 'in'
  });

  // In ElasticSearch: Eclose.ronPlatform has ronPlatform for RON closings and closingType for all other closings
  if (showRONClosing) {
    const includedRonPlatforms = [];
    if (showRONBSRClosing) {
      includedRonPlatforms.push('BLEND_SIGNING_ROOM');
    }
    if (showRONNotaryCamClosing) {
      includedRonPlatforms.push('NOTARYCAM');
    }
    if (showRONOtherClosing) {
      // null means no value set for Eclose.ronPlatform
      includedRonPlatforms.push(null);
    }
    if (showHybridClosing) {
      includedRonPlatforms.push('HYBRID');
    }
    if (showTraditionalClosing) {
      includedRonPlatforms.push('TRADITIONAL');
    }
    filters.push({
      columnField: 'Eclose.ronPlatform',
      content: includedRonPlatforms,
      operator: 'in'
    });
  }
  if (showClosingsWithTasks) {
    filters.push({
      columnField: 'Eclose.openTasksForOpsDesk',
      content: 1,
      operator: 'gte'
    });
  }
  if (enableAutoAssignment) {
    if (opsDeskAssigneeId) {
      filters.push({
        columnField: 'Eclose.opsDeskAgentId',
        content: opsDeskAssigneeId,
        operator: 'eq'
      });
    }
  } else {
    if (opsDeskAssigneeId) {
      filters.push({
        columnField: 'Eclose.opsDeskAgentId',
        content: opsDeskAssigneeId,
        operator: 'eq'
      });
    } else if (showUnassignedClosings) {
      filters.push({
        columnField: 'Eclose.opsDeskAgentId',
        content: EMPTY_ASSIGNED_CLOSINGS_FILTER_CONTENT,
        operator: 'in'
      });
    }
  }
  if (showCurrentClosings) {
    filters.push({
      columnField: 'Eclose.closingDatePipeline',
      content: getLatestUSTimezoneStartMinusDays(NUM_DAYS_INCLUDED_AS_PRESENT),
      operator: 'gte'
    });
  }
  if (showSentClosings) {
    filters.push({
      columnField: 'Eclose.closingStatus',
      content: DEFAULT_OPS_DESK_VIEWABLE_CLOSING_STATUSES,
      operator: 'in'
    });
  }
  return filters;
};
export const getFiltersForSettlementAgent = filterParams => {
  const filters = [];
  const {
    filterPreset
  } = filterParams;
  switch (filterPreset) {
    case FilterPreset.TODAY_CLOSINGS_WITH_OUTSTANDING_ESIGN_DOCS:
      const today = getClosingDateRange(0);
      filters.push({
        columnField: 'Eclose.closingDatePipeline',
        content: {
          gte: today.startMs,
          lte: today.endMs
        },
        operator: 'range'
      }, {
        columnField: 'Eclose.closingType',
        content: ['HYBRID', 'RON'],
        operator: 'in'
      }, {
        columnField: 'Eclose.closingStatus',
        content: ['SENT', 'SENT_DAY_OF', 'COMPLETED'],
        operator: 'in'
      }, {
        columnField: 'Eclose.closingPackageStatusPipeline',
        content: 'Complete',
        operator: 'ne'
      });
      break;
    case FilterPreset.IMMEDIATE_CLOSINGS_WITH_DOCS:
      const todayAndNextDay = getClosingDateRange(1);
      filters.push({
        columnField: 'Eclose.closingDatePipeline',
        content: {
          gte: todayAndNextDay.startMs,
          lte: todayAndNextDay.endMs
        },
        operator: 'range'
      }, {
        columnField: 'Eclose.closingStatus',
        content: ['SENT', 'SENT_DAY_OF', 'COMPLETED'],
        operator: 'in'
      });
      break;
    case FilterPreset.TODAY_AND_EARLIER_CLOSINGS_WITH_OPEN_TASKS:
      const todayDateRange = getClosingDateRange(0);
      filters.push({
        columnField: 'Eclose.closingDatePipeline',
        content: {
          lte: todayDateRange.endMs
        },
        operator: 'range'
      }, {
        columnField: 'Eclose.closingStatus',
        content: ['SENT', 'SENT_DAY_OF', 'COMPLETED'],
        operator: 'in'
      }, {
        columnField: 'Eclose.openTasksForSettlementAgent',
        content: 1,
        operator: 'gte'
      });
      break;
  }
  return filters;
};

/**
 * Removes OpsDesk Assignee information from filters storage data.
 * Common helper to remove opsDeskAssigneeId/opsDeskAssignment from
 * local storage.
 * @param  {FilterParams}                                       currentFilters current filters data
 * @param  {React.Dispatch<React.SetStateAction<FilterParams>>} filterDispatch State Dispatch method
 * @return {void}
 * @function
 */
export const clearOpsDeskAssignee = (currentFilters, filterDispatch) => {
  currentFilters.opsDeskAssignment = undefined;
  filterDispatch({
    ...currentFilters,
    opsDeskAssigneeId: ''
  });
};

/**
 * Sets the default value of the assignee dropdown to the principalId and name of the logged in user in pipeline filter.
 * @param currentFilters
 * @param filterDispatch
 * @param defaultAssignment
 * @param loggedInUser
 */
export const setDefaultOpsDeskAssignee = (currentFilters, filterDispatch, defaultAssignment, loggedInUser) => {
  const {
    label,
    value
  } = defaultAssignment;
  if (currentFilters.opsDeskAssignment) {
    currentFilters.opsDeskAssignment.principalId = value;
    currentFilters.opsDeskAssignment.name = label;
  }
  currentFilters.opsDeskAssigneeId = loggedInUser?.principalId || '';
  filterDispatch({
    ...currentFilters
  });
};